import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { SocialLinks } from "./SocialLinks";
import { useLang } from "../context/LanguageContext";
import { useSettings } from "../context/SettingsContext";
import { Logo } from "./Logo";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

export const Footer = () => {
  const { t, tc } = useLang();
  const { settings } = useSettings();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const subscribe = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", {
        name: "Investor Briefing Subscriber",
        email,
        type: "newsletter",
        interest: "Investor Insights",
      });
      toast.success(t.footer.subscribeSuccess);
      setEmail("");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || t.footer.subscribeError);
    } finally {
      setSubmitting(false);
    }
  };

  const exploreLinks = [
    { key: "projects", to: "/projects" },
    { key: "packages", to: "/packages" },
    { key: "villa", to: "/villa-construction" },
    { key: "agriInfra", to: "/agricultural-infrastructure" },
  ];
  const companyLinks = [
    { key: "about", to: "/about" },
    { key: "investmentModel", to: "/investment-model" },
    { key: "regions", to: "/regions" },
    { key: "media", to: "/media" },
  ];

  return (
    <footer className="bg-charcoal text-white relative overflow-hidden" data-testid="footer">
      <div className="absolute inset-0 grain" />
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-20 pb-10 relative">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2 min-w-0">
            <Logo to="/" height={50} className="mb-5" />
            <p className="text-white/60 text-sm leading-relaxed max-w-sm mb-6 break-words">{t.footer.tagline}</p>
            <div className="space-y-3 text-sm text-white/70">
              <a href={`mailto:${settings.email}`} data-testid="footer-email-link" className="flex items-center gap-3 hover:text-gold transition-colors min-w-0">
                <Mail className="w-4 h-4 text-gold shrink-0" /> <span dir="ltr" className="[unicode-bidi:plaintext] break-words [overflow-wrap:anywhere] min-w-0">{settings.email}</span>
              </a>
              <a href={`tel:${settings.phone}`} data-testid="footer-phone-link" className="flex items-center gap-3 hover:text-gold transition-colors">
              <Phone className="w-4 h-4 text-gold shrink-0" /> <span dir="ltr" className="[unicode-bidi:plaintext] break-words min-w-0">{settings.phone}</span>
              </a>
              <span className="flex items-center gap-3"><MapPin className="w-4 h-4 text-gold shrink-0" /> <span className="break-words min-w-0">{tc(settings.address)}</span></span>
            </div>
            <SocialLinks theme="dark" place="footer" className="mt-6" />
          </div>

          <div>
            <h4 className="overline mb-5">{t.footer.explore}</h4>
            <ul className="space-y-3">
              {exploreLinks.map((l) => (
                <li key={l.key}>
                  <Link to={l.to} className="text-white/70 hover:text-gold transition-colors text-sm">{t.nav[l.key]}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="overline mb-5">{t.footer.company}</h4>
            <ul className="space-y-3">
              {companyLinks.map((l) => (
                <li key={l.key}>
                  <Link to={l.to} className="text-white/70 hover:text-gold transition-colors text-sm">{t.nav[l.key]}</Link>
                </li>
              ))}
              <li><Link to="/faq" className="text-white/70 hover:text-gold transition-colors text-sm">{t.nav.faq}</Link></li>
            </ul>
          </div>

          <div className="min-w-0">
            <h4 className="overline mb-5">{t.footer.newsletter}</h4>
            <p className="text-white/60 text-sm mb-4 break-words">{t.footer.newsletterSub}</p>
            <form
              onSubmit={subscribe}
              className="flex items-center rounded-full bg-white/5 border border-white/10 overflow-hidden pe-1 me-14 sm:me-0"
            >
              <input
                type="email"
                dir="ltr"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.contact.email}
                data-testid="newsletter-input"
                className="bg-transparent text-sm text-white px-4 py-2.5 flex-1 min-w-0 w-full outline-none placeholder:text-white/40"
              />
              <button type="submit" disabled={submitting} aria-label={t.footer.subscribe} data-testid="newsletter-submit" className="w-9 h-9 rounded-full bg-gold flex items-center justify-center hover:bg-gold-soft transition-colors disabled:opacity-60">
                <ArrowRight className="w-4 h-4 text-charcoal" />
              </button>
            </form>
          </div>
        </div>

        <div className="mt-16 pt-8 pb-16 sm:pb-0 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-xs">© {new Date().getFullYear()} Smart Farmland A.Ş. {t.footer.rights}</p>
          <div className="flex items-center gap-6 text-xs text-white/40">
            <span className="hover:text-gold cursor-pointer transition-colors">{t.footer.privacy}</span>
            <span className="hover:text-gold cursor-pointer transition-colors">{t.footer.terms}</span>
            <span className="hover:text-gold cursor-pointer transition-colors">{t.footer.cookies}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
